import api from './api';
export { ORDER_STATUSES, type OrderItemStatus, type OrderStatus } from '@/types/orderStatus';
import type { OrderItemStatus, OrderStatus } from '@/types/orderStatus';

export interface OrderListAdminComment {
  readonly itemId: string;
  readonly requestedAt: string;
  readonly message: string;
}

export type FulfillmentMode = 'SHIPPING' | 'PICKUP';

export interface OrderListItem {
  readonly id: string;
  readonly displayCode: string;
  readonly status: OrderStatus;
  readonly fulfillmentMode?: FulfillmentMode;
  readonly createdAt: string;
  readonly confirmedAt?: string | null;
  readonly trackingNumber?: string | null;
  readonly shippingProvider?: string | null;
  readonly papers: ReadonlyArray<string>;
  readonly itemCount?: number | null;
  readonly destination: {
    readonly countryCode?: string | null;
    readonly region?: string | null;
    readonly city?: string | null;
    readonly postalCode?: string | null;
  } | null;
  readonly totalAmount: {
    readonly value: number;
    readonly currency: string;
  };
  readonly thumbnailUrl?: string;
  readonly previewUrls?: ReadonlyArray<string>;
  readonly adminComments?: ReadonlyArray<OrderListAdminComment>;
}

export interface UkrposhtaTrackingStatus {
  readonly barcode: string;
  readonly step?: number;
  readonly date?: string;
  readonly index?: string;
  readonly name?: string;
  readonly event?: number | string;
  readonly eventName?: string;
  readonly country?: string;
  readonly eventReason?: string | null;
  readonly eventReason_id?: number;
  readonly mailType?: number;
  readonly indexOrder?: number;
}

export interface OrderListParams {
  readonly statuses?: ReadonlyArray<OrderStatus>;
  readonly cursor?: string;
  readonly limit?: number;
}

export interface ShippingAddressPayload {
  readonly countryCode: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly phone: string;
  readonly street1: string;
  readonly street2?: string;
  readonly city: string;
  readonly novaposhtaCityRef?: string;
  readonly novaposhtaWarehouseId?: string;
  readonly deliveryMode?: 'BRANCH' | 'LOCKER' | 'COURIER' | 'PICKUP_POINT';
  readonly shippingProvider?: string;
  readonly region: string;
  readonly postalCode: string;
  readonly setAsDefault?: boolean;
}

export interface PickupContactPayload {
  readonly countryCode: string;
  readonly firstName: string;
  readonly lastName?: string;
  readonly phone: string;
  readonly setAsDefault?: boolean;
}

export type CheckoutContactPayload = ShippingAddressPayload | PickupContactPayload;

export interface PendingOrderItem {
  readonly id: string;
  readonly uploadedFileId?: string | null;
  readonly quantity?: number | null;
  readonly paperTypeId?: string | null;
  readonly printerId?: string | null;
  readonly widthIn?: number | null;
  readonly heightIn?: number | null;
  readonly marginIn?: number | null;
  readonly galleryWrapIn?: number | null;
  readonly galleryWrapMirrorEdges?: boolean | null;
  readonly printQualityWarning?: PrintQualityWarning | null;
  readonly colorMode?: AutosaveColorMode | null;
  readonly status: OrderItemStatus;
  readonly orderId: string | null;
  readonly originalWidthPx: number | null;
  readonly originalHeightPx: number | null;
  readonly dpiX: number | null;
  readonly dpiY: number | null;
  readonly originalWidthIn: number | null;
  readonly originalHeightIn: number | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly priceUsd?: number | null;
}

export interface PendingOrderSnapshot {
  readonly order: {
    readonly id?: string | null;
    readonly displayCode?: string | null;
    readonly status?: OrderStatus;
    readonly packaging?: string | null;
    readonly comment?: string | null;
    readonly createdAt?: string;
    readonly confirmedAt?: string | null;
    readonly updatedAt?: string;
    readonly shippingAddress?: {
      readonly countryCode?: string | null;
      readonly region?: string | null;
      readonly city?: string | null;
      readonly novaposhtaCityRef?: string | null;
      readonly novaposhtaWarehouseId?: string | null;
      readonly deliveryMode?: 'BRANCH' | 'LOCKER' | 'COURIER' | 'PICKUP_POINT' | null;
      readonly shippingProvider?: string | null;
      readonly postalCode?: string | null;
    } | null;
    readonly paymentInformation?: unknown | null;
    readonly fulfillmentMode?: FulfillmentMode;
    readonly items: ReadonlyArray<PendingOrderItem>;
  };
}

interface OrderListApiResponse {
  readonly data: {
    readonly orders: ReadonlyArray<OrderListItem>;
    readonly nextCursor: string | null;
  };
}

export interface Order extends Omit<OrderListItem, 'papers' | 'thumbnailUrl' | 'previewUrls'> {
  readonly orderItems: ReadonlyArray<PendingOrderItem>;
  readonly pricingSummary: OrderPricingSummary;
}

interface OrderApiResponse {
  readonly data: Order;
}

interface UkrposhtaTrackingApiResponse {
  readonly data: {
    readonly orderId: string;
    readonly trackingNumber: string;
    readonly provider: string;
    readonly lang: 'UA' | 'EN';
    readonly availability: 'AVAILABLE' | 'NOT_FOUND' | 'UNAVAILABLE';
    readonly statuses: ReadonlyArray<UkrposhtaTrackingStatus>;
  };
}

export type AutosaveColorMode = 'COLOR' | 'BLACK_WHITE';

export interface PrintQualityWarning {
  readonly type: 'LOW_PPI';
  readonly ppi: number;
  readonly threshold: number;
  readonly acknowledged?: boolean;
  readonly reason?: string;
}

export interface OrderPricingSummary {
  readonly currency: string;
  readonly itemsSubtotal: number;
  readonly total: number;
  readonly pricedItemCount: number;
  readonly itemCount: number;
  readonly hasUnpricedItems: boolean;
}

export interface OrderItemAutosavePatchPayload {
  orderItemId: string;
  quantity?: number;
  paperTypeId?: string | null;
  printerId?: string | null;
  widthIn?: number | null;
  heightIn?: number | null;
  marginIn?: number | null;
  galleryWrapIn?: number | null;
  galleryWrapMirrorEdges?: boolean | null;
  printQualityWarning?: PrintQualityWarning | null;
  colorMode?: AutosaveColorMode | null;
}

export interface OrderItemAutosaveResponse {
  id: string;
  uploadedFileId?: string | null;
  quantity?: number | null;
  paperTypeId?: string | null;
  printerId?: string | null;
  widthIn?: number | null;
  heightIn?: number | null;
  marginIn?: number | null;
  galleryWrapIn?: number | null;
  galleryWrapMirrorEdges?: boolean | null;
  printQualityWarning?: PrintQualityWarning | null;
  colorMode?: AutosaveColorMode | null;
  priceUsd?: number | null;
  updatedAt?: string;
}

export interface CreateOrderItemPayload {
  readonly orderItemId?: string;
  readonly uploadedFileId: string;
  readonly quantity?: number;
  readonly paperTypeId: string;
  readonly printerId: string;
  readonly widthIn: number;
  readonly heightIn: number;
  readonly marginIn?: number | null;
  readonly galleryWrapIn?: number | null;
  readonly galleryWrapMirrorEdges?: boolean | null;
  readonly colorMode?: AutosaveColorMode | null;
}

export interface CreateOrderPayload {
  readonly serviceId: string;
  readonly items: ReadonlyArray<CreateOrderItemPayload>;
  readonly packaging?: string | null;
  readonly comment?: string | null;
  readonly shippingAddress?: CheckoutContactPayload;
  readonly fulfillmentMode?: FulfillmentMode;
}

export interface CreatedOrderItem {
  readonly id: string;
  readonly uploadedFileId?: string | null;
  readonly quantity?: number | null;
  readonly paperTypeId?: string | null;
  readonly printerId?: string | null;
  readonly widthIn?: number | null;
  readonly heightIn?: number | null;
  readonly marginIn?: number | null;
  readonly galleryWrapIn?: number | null;
  readonly galleryWrapMirrorEdges?: boolean | null;
  readonly printQualityWarning?: PrintQualityWarning | null;
  readonly colorMode?: AutosaveColorMode | null;
  readonly priceUsd?: number | null;
  readonly status?: string | null;
  readonly updatedAt?: string | null;
  readonly [key: string]: unknown;
}

export interface CreateOrderResult {
  readonly id: string;
  readonly status?: string | null;
  readonly packaging?: string | null;
  readonly comment?: string | null;
  readonly pricingSummary?: OrderPricingSummary;
  readonly paymentInformation?: unknown | null;
  readonly orderItems: ReadonlyArray<CreatedOrderItem>;
  readonly [key: string]: unknown;
}

export interface UpdateOrderItemPayload {
  readonly id: string;
  readonly status?: string | null;
  readonly uploadedFileId?: string;
  readonly quantity?: number;
  readonly paperTypeId?: string;
  readonly printerId?: string;
  readonly widthIn?: number;
  readonly heightIn?: number;
  readonly marginIn?: number | null;
  readonly galleryWrapIn?: number | null;
  readonly galleryWrapMirrorEdges?: boolean | null;
  readonly printQualityWarning?: PrintQualityWarning | null;
  readonly colorMode?: AutosaveColorMode | null;
}

export interface UpdateOrderPayload {
  readonly items: ReadonlyArray<UpdateOrderItemPayload>;
  readonly packaging?: string | null;
  readonly comment?: string | null;
  readonly shippingAddress?: CheckoutContactPayload;
  readonly fulfillmentMode?: FulfillmentMode;
}

const buildQuery = (params: OrderListParams) => {
  const query: Record<string, unknown> = {};
  if (params.limit) query.limit = params.limit;
  if (params.cursor) query.cursor = params.cursor;
  if (params.statuses && params.statuses.length > 0) {
    query.statuses = [...params.statuses];
  }
  return query;
};

export const fetchOrder = async (orderId: string): Promise<Order> => {
  const { data } = await api.get<OrderApiResponse>(`/orders/${orderId}`);
  return data.data;
};

export const fetchUkrposhtaTracking = async (
  orderId: string,
  lang: 'UA' | 'EN',
): Promise<UkrposhtaTrackingApiResponse['data']> => {
  const { data } = await api.get<UkrposhtaTrackingApiResponse>(`/orders/${orderId}/ukrposhta-tracking`, {
    params: { lang },
  });
  return data.data;
};

export const fetchOrders = async (
  params: OrderListParams = {},
): Promise<{ orders: ReadonlyArray<OrderListItem>; nextCursor: string | null }> => {
  const { data } = await api.get<OrderListApiResponse>('/orders', {
    params: buildQuery(params),
  });
  return data.data;
};

export const deleteOrder = async (orderId: string): Promise<void> => {
  await api.delete(`/orders/${orderId}`);
};

export const deleteOrderItem = async (orderItemId: string): Promise<void> => {
  await api.delete(`/orders/items/${orderItemId}`);
};

export const fetchPendingOrderItems = async (): Promise<PendingOrderSnapshot> => {
  const { data } = await api.get<{ data: PendingOrderSnapshot }>(
    '/orders/pending-items',
  );
  return data.data;
};

export type SavedShippingAddress = ShippingAddressPayload & { id: string };

type DefaultShippingAddress = SavedShippingAddress;

const normalizeSavedShippingAddress = (payload: SavedShippingAddress & { street2?: string | null }): SavedShippingAddress => {
  const { street2, setAsDefault = true, ...rest } = payload;
  return {
    ...rest,
    street2: street2 ?? undefined,
    setAsDefault,
  };
};

export const fetchDefaultShippingAddress = async (): Promise<DefaultShippingAddress | null> => {
  const { data } = await api.get<{ data: (DefaultShippingAddress & { street2?: string | null }) | null }>(
    '/orders/default-address',
  );

  const payload = data.data;
  if (!payload) {
    return null;
  }

  return normalizeSavedShippingAddress(payload) as DefaultShippingAddress;
};

export const fetchSavedShippingAddresses = async (): Promise<ReadonlyArray<SavedShippingAddress>> => {
  const { data } = await api.get<{ data: ReadonlyArray<SavedShippingAddress & { street2?: string | null }> }>(
    '/orders/saved-addresses',
  );

  return Array.isArray(data.data) ? data.data.map((entry) => normalizeSavedShippingAddress(entry)) : [];
};

export const createOrder = async (payload: CreateOrderPayload): Promise<CreateOrderResult> => {
  const { data } = await api.post<{ data: CreateOrderResult }>('/orders', payload);
  return data.data;
};

export const updateOrder = async (orderId: string, payload: UpdateOrderPayload): Promise<CreateOrderResult> => {
  const { data } = await api.patch<{ data: CreateOrderResult }>(`/orders/${orderId}`, payload);
  return data.data;
};

export interface ShippingSelectionPayload {
  readonly provider: string;
  readonly option: string;
  readonly fingerprint: string;
}

export interface WithdrawOrderBalanceResponse {
  readonly payment: { id: string; amount: number; currency: string; createdAt: string };
  readonly balance: number;
  readonly shipping?: {
    readonly amountUsd: number;
    readonly originalAmount: number;
    readonly originalCurrency: string;
    readonly provider: string;
    readonly option: string;
    readonly fingerprint: string;
    readonly status: 'OK' | 'FALLBACK';
    readonly statusLabel?: string;
  };
}

export const withdrawOrderBalance = async (
  orderId: string,
  payload?: { shippingSelection?: ShippingSelectionPayload | null; promoCodes?: string[] },
): Promise<WithdrawOrderBalanceResponse> => {
  const { data } = await api.post<{ data: WithdrawOrderBalanceResponse }>(
    `/orders/${orderId}/withdraw`,
    payload ?? {},
  );
  return data.data;
};

export const transitionOrderStatusForUser = async (
  orderId: string,
  status: string,
  options: { shippingAddress?: CheckoutContactPayload; fulfillmentMode?: FulfillmentMode } = {},
): Promise<CreateOrderResult> => {
  const payload: Record<string, unknown> = { status };
  if (options.shippingAddress) {
    payload.shippingAddress = options.shippingAddress;
  }
  if (options.fulfillmentMode) {
    payload.fulfillmentMode = options.fulfillmentMode;
  }

  const { data } = await api.post<{ data: CreateOrderResult }>(`/orders/${orderId}/transition`, payload);
  return data.data;
};

export const patchOrderItems = async (
  patches: ReadonlyArray<OrderItemAutosavePatchPayload>,
): Promise<ReadonlyArray<OrderItemAutosaveResponse>> => {
  if (patches.length === 0) {
    return [];
  }

  const { data } = await api.patch<{ data: ReadonlyArray<OrderItemAutosaveResponse> }>(
    '/orders/items',
    { patches: [...patches] },
  );

  return data.data;
};
